import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Platform } from '@ionic/angular';

import { IBaseInputContract } from '../contracts/base-input';

@Component({
  selector: 'itl-text',
  templateUrl: './text.component.html',
  styleUrls: ['../_templates/text-input-template/text-input-template-alb-class.scss', './text.component.scss'],
  standalone: false,
})
export class TextComponent implements OnInit, IBaseInputContract, ControlValueAccessor {
  @Input() public title: string;
  @Input() public placeholder: string;
  @Input() public icon: string;
  @Input() public type: 'tel' | 'text' | 'email' | 'number' | 'password' | 'url' = 'text';
  // zeigt einen Counter an wie viele Zeichen noch möglich sind und sperrt weitere eingaben
  @Input() public maxlength: number;
  // Automatisch jeden ersten Buchstaben eines Wortes groß schreiben
  @Input() public capatalizeFirstLetter = false;
  // Zeigt einen roten Stern neben dem Titel an
  @Input() public required = false;
  // Es wird kein Icon angezeigt
  @Input() public noIcon = false;
  // Graut die Komponente aus und keine Interaktion mehr möglich - stetzt auch die readonly property auf den Text Input
  @Input()
  public set disabled(value: boolean) {
    this._disabledByInput = !!value;
  }
  public get disabled(): boolean {
    return this._disabledByInput || this._disabledByControl;
  }
  @Input() public description: string;
  @Input() public trimEnabled = false;
  @Input() public hideDelete = false;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('data-testid') public dataTestId: string;
  @Input() public allowNegative = true;
  @Output() public change: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('input', { static: true }) public input: ElementRef;

  public value = '';
  public isIos: boolean;
  public onChange = (text: string) => {};
  public onTouched = () => {};

  private _disabledByInput = false;
  private _disabledByControl = false;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private _changeDetectorRef: ChangeDetectorRef,
    platform: Platform
  ) {
    this.isIos = platform.is('ios');
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.placeholder = this.type === 'tel' ? '0....' : this.placeholder || '';
  }

  onInputChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    let newValue = inputElement.value;

    if (this.capatalizeFirstLetter) {
      newValue = this.toTitleCase(newValue);
    }

    if (this.type === 'number' && !this.allowNegative) {
      // Convert value to a number to check for negativity
      const numericValue = parseFloat(newValue);
      if (numericValue < 0 || isNaN(numericValue)) {
        newValue = '0';
      }
    }

    // Only update if value has changed
    if (newValue !== inputElement.value) {
      // Update the input element value
      inputElement.value = newValue;
    }

    // Update the model and notify Angular
    this.value = newValue;
    this.onChange(this.value);
    this.change.emit(this.value);
  }

  public setFocus() {
    this.input.nativeElement.focus();
  }

  public markAsTouched(): void {
    this.onTouched();
  }

  public writeValue(obj: any) {
    if (this.type === 'number' && parseFloat(obj) < 0 && !this.allowNegative) {
      obj = '0';
    }
    this.input.nativeElement.value = obj ?? '';
    this._changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabledByControl = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  private toTitleCase(text: string): string {
    const spaceSplit = text.toLowerCase().split(' ');
    for (let i = 0; i < spaceSplit.length; i++) {
      spaceSplit[i] = spaceSplit[i].charAt(0).toUpperCase() + spaceSplit[i].slice(1);

      // Handle names with hyphens
      const hyphenSplit = spaceSplit[i].split('-');
      for (let j = 1; j < hyphenSplit.length; j++) {
        hyphenSplit[j] = `${hyphenSplit[j].charAt(0).toLocaleUpperCase()}${hyphenSplit[j].slice(1)}`;
      }

      spaceSplit[i] = hyphenSplit.join('-');
    }
    return spaceSplit.join(' ');
  }

  public removeSelected($event): void {
    if ($event) {
      $event.stopPropagation();
    }
    // Update internal value first
    this.value = null;
    // Update the input element
    this.writeValue(null);
    // Notify Angular about the change
    this.onChange(this.value);
    this.change.emit(this.value);
    // Mark as touched to trigger validation
    this.onTouched();
  }
}
